import React, { useEffect, useState } from "react";
import { Fab, Modal, Paper, Tooltip, useTheme } from "@mui/material";
import { Box } from "@mui/system";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { subscribeToRelay } from "./relay";
import { selectMissionSelect } from "../mission/missionSlice";
import LayersIcon from "@mui/icons-material/Layers";
import FlightIcon from "@mui/icons-material/Flight";
import {
  getGeofenceListThunk,
  getLandingZoneListThunk,
  listLandingPadsThunk,
} from "../airspace/airspaceSlice";
import { FloatingAircraft } from "./FloatingAircraft";
import { Menu } from "../../components/Menu";
import { AircraftMenu } from "../../components/selectAircraft/AircraftMenu";
import { CommandTrayAll } from "../../components/CommandTrayAll";
import { useInterval } from "../../useInterval";
import { MapboxMap } from "../../components/mapbox/MapboxMap";
import { SseConnectionStatus } from "../adsb/adsbTypes";

const Overview = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const missionSelect = useAppSelector(selectMissionSelect);
  const [showTiles, setShowTiles] = useState(false);
  const [showTraffic, setShowTraffic] = useState(true);
  const [sseStatus, setSseStatus] = useState<SseConnectionStatus>('disconnected');
  const fabRef = React.useRef<HTMLButtonElement | null>(null);
  const trafficFabRef = React.useRef<HTMLButtonElement | null>(null);

  const toggleTiles = () => {
    setShowTiles((prev) => !prev);
  };

  const toggleTraffic = () => {
    setShowTraffic((prev) => !prev);
  };

  useInterval(() => {
    dispatch(listLandingPadsThunk({}));
  }, 30000);

  useEffect(() => {
    dispatch(subscribeToRelay());
    dispatch(listLandingPadsThunk({}));
    dispatch(getGeofenceListThunk({}));
    dispatch(getLandingZoneListThunk({}));
    return () => {};
  }, []);

  const onClickFab = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    toggleTiles();
  };

  useEffect(() => {
    const handleTouch = (e: TouchEvent) => {
      e.preventDefault();
      setShowTiles((prev) => !prev);
    };

    const element = fabRef.current;
    element?.addEventListener("touchstart", handleTouch, { passive: false });

    return () => {
      element?.removeEventListener("touchstart", handleTouch);
    };
  }, []);

  useEffect(() => {
    const handleTrafficTouch = (e: TouchEvent) => {
      e.preventDefault();
      setShowTraffic((prev) => !prev);
    };

    const element = trafficFabRef.current;
    element?.addEventListener("touchstart", handleTrafficTouch, { passive: false });

    return () => {
      element?.removeEventListener("touchstart", handleTrafficTouch);
    };
  }, []);

  return (
    <>
      <Paper style={{ height: "100%", width: "100%" }}>
        <Box sx={{ height: "100vh" }} display={"flex"} flexDirection="row">
          <MapboxMap showTiles={showTiles} showTraffic={showTraffic} onSseStatusChange={setSseStatus} />
        </Box>
        <Box
          sx={{
            height: "100vh",
            width: "100vw",
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            borderRadius: 0,
            backgroundColor: "transparent",
            pointerEvents: "none",
            zIndex: 500,
          }}
        >
          <FloatingAircraft />
        </Box>
        <Box
          sx={{
            position: "absolute",
            pointerEvents: "none",

            left: 0,
            bottom: 0,
            borderRadius: 0,
            bgcolor: "transparent",
            zIndex: 500,
          }}
        >
          <Menu />
        </Box>
        <Box
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            borderRadius: 0,
            bgcolor: "transparent",
            zIndex: 500,
            pointerEvents: "none",
          }}
        >
          <AircraftMenu />
        </Box>
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            borderRadius: 0,
            bgcolor: "transparent",
            zIndex: 500,
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 2,
            padding: 2,
          }}
        >
          <Tooltip title="Toggle satellite tiles" placement="left">
            <Fab
              ref={fabRef}
              sx={{
                pointerEvents: "all",
                backgroundColor: theme.palette.background.paper,
              }}
              aria-label={"toggle-tiles"}
              color={"default"}
              onClick={onClickFab}
            >
              <LayersIcon fontSize={"large"} color="primary" />
            </Fab>
          </Tooltip>
          <Tooltip title={showTraffic ? "Hide ADS-B traffic" : "Show ADS-B traffic"} placement="left">
            {/* marginTop leaves room for the map recenter FAB in MapboxMap */}
            <Box sx={{ position: "relative", marginTop: "80px" }}>
              <Fab
                ref={trafficFabRef}
                sx={{
                  pointerEvents: "all",
                  backgroundColor: showTraffic
                    ? theme.palette.background.paper
                    : "transparent",
                }}
                aria-label={"toggle-traffic"}
                color={"default"}
                onClick={toggleTraffic}
              >
                <FlightIcon
                  fontSize={"large"}
                  sx={{
                    color: "white",
                  }}
                />
              </Fab>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  zIndex: 9999,
                  pointerEvents: "none",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: "1.5px solid",
                  borderColor: theme.palette.background.paper,
                  backgroundColor:
                    sseStatus === "connected"
                      ? "#00ff00"
                      : sseStatus === "connecting"
                      ? "#ffff00"
                      : sseStatus === "error"
                      ? "#ff0000"
                      : "#888888",
                }}
              />
            </Box>
          </Tooltip>
          <CommandTrayAll />
        </Box>
      </Paper>

      <Modal open={!!missionSelect}>
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            height: 500,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            pointerEvents: "none",
          }}
        ></Box>
      </Modal>
    </>
  );
};

export const OverviewMap = Overview;
